<template>
  <div class="mt-4 yield-asset-list">
    <div
      class="d-flex align-center bg-primary-lighter has-border border-color-primary-lighter rounded-8 gray--text text-6 font-weight-medium px-2 px-md-6 py-2"
    >
      <div class="d-flex align-center flex-3">
        <div class="flex-2">Asset</div>
        <div class="flex-1">
          <div
            class="d-flex justify-end align-center clickable"
            @click="setOrder('price')"
          >
            <div class="mr-1" v-if="sort === 'price'">
              <v-icon x-small v-if="order > 0">mdi-arrow-up</v-icon>
              <v-icon x-small v-else>mdi-arrow-down</v-icon>
            </div>
            <div>Price</div>
          </div>
        </div>
        <div class="flex-2 text-right">
          <div
            class="d-flex justify-end align-center clickable"
            @click="setOrder('tvl')"
          >
            <div class="mr-1" v-if="sort === 'tvl'">
              <v-icon x-small v-if="order > 0">mdi-arrow-up</v-icon>
              <v-icon x-small v-else>mdi-arrow-down</v-icon>
            </div>
            <div class="d-none d-md-block">Total Value Locked</div>
            <div class="d-block d-md-none">TVL</div>
          </div>
        </div>
        <div class="flex-1 text-right">
          <div
            class="d-flex justify-end align-center clickable"
            @click="setOrder('apy')"
          >
            <div class="mr-1" v-if="sort === 'apy'">
              <v-icon x-small v-if="order > 0">mdi-arrow-up</v-icon>
              <v-icon x-small v-else>mdi-arrow-down</v-icon>
            </div>
            <div>APY</div>
          </div>
        </div>
      </div>
      <div class="align-center flex-2 d-none d-md-flex">
        <div class="flex-2 text-right">Product</div>
        <div class="flex-1 text-center">Ecosystem</div>
        <div class="flex-1 text-center">Yield</div>
      </div>
    </div>
    <div
      v-for="item in displayedItems"
      :key="item.token"
      :class="[
        'd-flex flex-column flex-md-row align-md-center text-4 font-weight-medium px-2 px-md-6 py-4 has-divider clickable',
      ]"
      @click="$router.push({ path: `/${item.type}/${item.productName}` })"
    >
      <div class="d-flex align-center flex-3">
        <div class="flex-2 d-flex align-center">
          <img
            :src="item.logo"
            alt=""
            height="36"
            width="36"
            class="d-none d-sm-block mr-5"
          />
          <div>
            <div class="text-uppercase">{{ item.tokenName }}</div>
            <div class="text-6 gray--text mt-1">{{ item.text }}</div>
          </div>
        </div>
        <div class="flex-1 text-right">${{ item.price | currency }}</div>
        <div class="flex-2 text-right">${{ item.tvl | currency }}</div>
        <div class="flex-1 text-right">
          <v-tooltip bottom color="dark" v-if="item.variesApy">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                Varies
              </span>
            </template>
            <span>Dependent on transaction fees</span>
          </v-tooltip>
          <span v-else>{{ item.apy | numberFormat("0,0.00", false) }}%</span>
        </div>
      </div>
      <div class="d-flex align-center flex-2 mt-2 mt-md-0">
        <div class="flex-2">
          <div class="justify-start justify-md-end d-flex">
            <div
              :class="[
                'text-7 font-weight-medium px-2 py-1 rounded-5 black--text-abs',
                `bg-${item.type}`,
              ]"
            >
              {{ productMap[item.type] }}
            </div>
            <div
              v-if="item.type === 'vaults'"
              class="text-7 font-weight-medium px-2 py-1 rounded-5 bg-vaults ml-1"
            >
              <span class="primary--text">
                {{ item.tokenName.split("-")[1] }}
              </span>
            </div>
          </div>
          <div
            class="d-flex justify-start justify-md-end mt-2"
            v-if="item.migrationRequired"
          >
            <div class="migration-required">
              <v-icon x-small class="white--text-abs"
                >fas fa-info-circle</v-icon
              >
              <span class="ml-1 text-7">MIGRATION REQUIRED</span>
            </div>
          </div>
          <div
            class="d-flex justify-start justify-md-end mt-2"
            v-if="item.withdrawalRequired"
          >
            <div class="migration-required">
              <v-icon x-small class="white--text-abs"
                >fas fa-info-circle</v-icon
              >
              <span class="ml-1 text-7">WITHDRAWAL REQUIRED</span>
            </div>
          </div>
        </div>
        <div class="flex-1 text-center">
          <v-tooltip bottom color="dark">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <img
                  :src="item.market.logo"
                  height="20"
                  width="20"
                  :alt="item.market.name"
                />
              </span>
            </template>
            <span>{{ item.market.name }}</span>
          </v-tooltip>
        </div>
        <div class="flex-1 text-center" v-if="!item.isLegacy">
          <c-btn
            class="width-100"
            is-short
            is-narrow
            wallet-required
            @click.stop="
              $router.push({
                path: `/${item.type}/${item.productName}${
                  item.type === 'vaults'
                    ? '?action=deposit'
                    : '?action=tx&type=stake'
                }`,
              })
            "
          >
            <span v-if="item.type === 'vaults'">Deposit</span>
            <span v-else>Stake</span>
          </c-btn>
        </div>
        <div class="flex-1 text-center" v-else>
          <c-btn
            class="width-100"
            is-short
            is-narrow
            wallet-required
            @click.stop="
              $router.push({
                path: `/${item.type}/${item.productName}${
                  item.type === 'vaults'
                    ? '?action=withdraw'
                    : '?action=tx&type=unstake'
                }`,
              })
            "
          >
            <span v-if="item.type === 'vaults'">Withdraw</span>
            <span v-else>Unstake</span>
          </c-btn>
        </div>
      </div>
    </div>
    <div
      class="d-flex justify-center mt-10"
      v-if="displayedItems.length >= maxItems"
    >
      <c-btn is-default is-short is-narrow @click="showMore = !showMore">
        <span v-if="showMore">Show less</span>
        <span v-else>Load more</span>
      </c-btn>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    items: Array,
    sort: String,
    order: Number,
  },
  data: () => ({
    productMap: {
      stake: "PROOF-OF-STAKE",
      vaults: "DEFI VAULTS",
    },
    showMore: false,
    maxItems: 15,
  }),
  computed: {
    displayedItems() {
      return this.items.slice(
        0,
        this.showMore ? this.items.length : this.maxItems
      );
    },
  },
  methods: {
    setOrder(sort) {
      this.$emit("setOrder", {
        sort,
        order: sort === this.sort ? -1 * this.order : this.order,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.yield-asset-list {
  .has-divider {
    border-bottom: 1px solid #f9eae8;
  }
}
</style>
